/**
 * What a contact's fields are called, which record types can store each one,
 * where a value came from, and how an edit becomes a patch.
 *
 * The edit dialog this replaces submitted all eight of its fields on every
 * save, so a field the person never touched was rewritten anyway — and on a
 * captured row, pinned, which froze capture out of it for good. The stored
 * note went with it, because the page never handed the note in and the
 * dialog sent back the blank. `buildPatch` exists so that cannot recur: a
 * save sends the keys whose value differs from the record, and nothing else.
 *
 * The storable table mirrors the server contract (SPEC A2) for the four
 * record types. A field a type cannot store is drawn read-only with the
 * reason, rather than as an input whose save reports "saved" and changes
 * nothing — which is what the old dialog did on every master record.
 */
import { CalendarDays, Mail, PhoneCall, StickyNote, type LucideIcon } from 'lucide-react'

import {
  crmContactName,
  crmService,
  partyTypeFor,
  type CrmActivity,
  type CrmActivityKind,
  type CrmCaptureSummary,
  type CrmCommunication,
  type CrmCompanyType,
  type CrmContact,
  type CrmContactPatch,
  type CrmDealHistoryRow,
  type CrmDedupSummary,
  type CrmDuplicateCandidate,
  type CrmEnrichResponse,
} from '@/services/crm'
import { parseDateValue } from '@/utils/dates'

export const NOT_ON_RECORD = 'Not on record'

export type PartyType = ReturnType<typeof partyTypeFor>

// ── Labels ─────────────────────────────────────────────────────────────────

export const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  firstName: 'First name',
  lastName: 'Last name',
  displayName: 'Name',
  company: 'Company',
  title: 'Title',
  entityType: 'Entity type',
  companyType: 'Type',
  kind: 'Kind',
  notes: 'Notes',
  isEntity: 'Organization',
  isInternal: 'Internal',
  email: 'Email',
  emails: 'Other emails',
  phone: 'Phone',
  directPhone: 'Direct',
  mobilePhone: 'Mobile',
  officePhone: 'Office',
  address: 'Address',
  city: 'City',
  state: 'State',
  postalCode: 'Postal code',
  location: 'Location',
  linkedIn: 'LinkedIn',
  linkedin: 'LinkedIn',
  website: 'Website',
  status: 'Status',
  source: 'Source',
  tags: 'Markets',
  capacities: 'Capacities',
  assetClasses: 'Asset classes',
  isPreferred: 'Preferred',
  isBlocklisted: 'Blocklisted',
  yearsExperience: 'Years of experience',
  netWorth: 'Net worth',
  liquidity: 'Liquidity',
  flags: 'Flags',
  emailDomains: 'Email domains',
  companyDomain: 'Company domain',
  company_domain: 'Company domain',
  roleEvidence: 'Role evidence',
  dealVolume: 'Deal volume',
  education: 'Education',
  licenses: 'Licenses',
  previousCompanies: 'Previous companies',
  specialties: 'Specialties',
  news: 'News',
  formattedAddress: 'Address (maps)',
  firmPageUrl: 'Firm page',
  alternateEmail: 'Alternate email',
  siteTitle: 'Site title',
  contactEmails: 'Contact emails',
  aboutUrl: 'About page',
  description: 'Description',
  twitter: 'Twitter',
  facebook: 'Facebook',
  instagram: 'Instagram',
  firstSeen: 'First seen',
  lastContact: 'Last seen',
}

/** A readable label for a field name, whether or not the table knows it. */
export function labelForField(field: string): string {
  const known = FIELD_LABELS[field]
  if (known) return known
  return observedLabel(field)
}

/**
 * The label for a value the lookup observed under a name the screen has no
 * slot for — "directPhone" reads "Direct Phone", "company_domain" reads
 * "Company Domain". Title case on purpose: these are the engine's own field
 * names shown as-is, not the screen's vocabulary.
 */
export function observedLabel(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase())
}

/** A broker master's relationship status, in the words the model stores
 *  (`Broker.relationship_status`: Active, Inactive, Blocked). */
export const CRM_STATUS_OPTIONS = ['Active', 'Inactive', 'Blocked'] as const

export function kindLabel(kind: CrmContact['kind'] | undefined, isEntity: boolean): string {
  if (kind === 'organisation' || (kind === undefined && isEntity)) return 'Organization'
  return 'Person'
}

// ── What each record type can store (SPEC A2) ──────────────────────────────

type PatchKey = keyof CrmContactPatch

const COMMON: PatchKey[] = ['name', 'notes', 'linkedIn', 'website', 'enrichment']

/**
 * The keys each record type has a column (or an enrichment slot) for.
 *
 * Excluded only where the contract says a type ignores the key; where it is
 * silent the key is offered and the server's `ignored` list has the last
 * word, so the screen never claims a field is unstorable on a guess.
 */
export const STORABLE_FIELDS_BY_PARTY_TYPE: Record<PartyType, ReadonlySet<PatchKey>> = {
  deal_contact: new Set<PatchKey>([
    ...COMMON,
    'company',
    'title',
    'companyType',
    'isEntity',
    'isInternal',
    'email',
    'emails',
    'phone',
    'directPhone',
    'mobilePhone',
    'officePhone',
    'address',
    'city',
    'state',
    'postalCode',
    'companyDomain',
    'roleEvidence',
  ]),
  broker: new Set<PatchKey>([
    ...COMMON,
    'company',
    'title',
    'email',
    'emails',
    'phone',
    'address',
    'status',
    'tags',
    'assetClasses',
    'isPreferred',
    'isBlocklisted',
  ]),
  broker_firm: new Set<PatchKey>([
    ...COMMON,
    'phone',
    'address',
    'city',
    'state',
    'tags',
    'emailDomains',
  ]),
  sponsor: new Set<PatchKey>([
    ...COMMON,
    'email',
    'phone',
    'entityType',
    'tags',
    'assetClasses',
    'yearsExperience',
    'netWorth',
    'liquidity',
    'flags',
  ]),
}

export function isStorable(partyType: PartyType, key: PatchKey): boolean {
  return STORABLE_FIELDS_BY_PARTY_TYPE[partyType].has(key)
}

// ── Form values and the diff-only patch ────────────────────────────────────

/** The enrichment slots a person may set by hand, as form text. */
export interface EnrichmentFormValues {
  yearsExperience: string
  dealVolume: string
  education: string
  licenses: string[]
  previousCompanies: string[]
  specialties: string[]
}

/**
 * Every editable field, in the shape an input holds it: strings for text and
 * numbers (an empty string is "not on record"), arrays for chips, booleans for
 * checkboxes. Built from a contact, edited in place, and diffed back against
 * the original by `buildPatch`.
 */
export interface ContactFormValues {
  name: string
  company: string
  title: string
  entityType: string
  companyType: CrmCompanyType
  notes: string
  isEntity: boolean
  isInternal: boolean
  email: string
  emails: string[]
  phone: string
  directPhone: string
  mobilePhone: string
  officePhone: string
  address: string
  city: string
  state: string
  postalCode: string
  linkedIn: string
  website: string
  status: string
  tags: string[]
  assetClasses: string[]
  isPreferred: boolean
  isBlocklisted: boolean
  yearsExperience: string
  netWorth: string
  liquidity: string
  flags: string[]
  emailDomains: string[]
  companyDomain: string
  roleEvidence: string
  enrichment: EnrichmentFormValues
}

const text = (value: unknown): string => (value === null || value === undefined ? '' : String(value))

/** Addresses beyond the primary one, without repeating it. */
export function otherEmails(contact: Pick<CrmContact, 'email' | 'emails'>): string[] {
  const primary = (contact.email ?? '').toLowerCase()
  return (contact.emails ?? []).filter((a) => a.toLowerCase() !== primary)
}

/** The form's starting values, read off the record without inventing any. */
export function formValuesFor(contact: CrmContact): ContactFormValues {
  const isSponsor = partyTypeFor(contact.id) === 'sponsor'
  const e = contact.enrichment
  const m = contact.metrics ?? {}
  return {
    name: [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim(),
    company: text(contact.company),
    // A sponsor master states its entity type, never a job title, so the
    // form shows it under the right name and never both.
    title: isSponsor ? '' : text(contact.title),
    entityType: isSponsor ? text(contact.entityType) : '',
    companyType: contact.companyType,
    notes: text(contact.notes),
    isEntity: Boolean(contact.isEntity),
    isInternal: Boolean(contact.isInternal),
    email: text(contact.email),
    emails: otherEmails(contact),
    phone: text(contact.phone ?? contact.phones?.primary),
    directPhone: text(contact.phones?.direct),
    mobilePhone: text(contact.phones?.mobile),
    officePhone: text(contact.phones?.office),
    address: text(contact.address),
    city: text(contact.city),
    state: text(contact.state),
    postalCode: text(contact.postalCode),
    linkedIn: text(e?.linkedIn),
    website: text(e?.website),
    status: text(contact.status),
    // `tags` is the folded read-only list; the record exposes the two lists
    // behind it separately so an edit to markets cannot migrate an asset
    // class. The patch key for markets is still `tags` (the server maps it
    // to the markets column).
    tags: [...(contact.markets ?? [])],
    assetClasses: [...(contact.assetClasses ?? [])],
    isPreferred: Boolean(m.isPreferred),
    isBlocklisted: Boolean(m.isBlocklisted),
    yearsExperience: text(m.yearsExperience),
    netWorth: text(m.netWorth),
    liquidity: text(m.liquidity),
    flags: [...(m.flags ?? [])],
    emailDomains: [...(contact.emailDomains ?? [])],
    companyDomain: text(contact.companyDomain),
    roleEvidence: text(contact.roleEvidence),
    enrichment: {
      yearsExperience: text(e?.yearsExperience),
      dealVolume: text(e?.dealVolume),
      education: text(e?.education),
      licenses: [...(e?.licenses ?? [])],
      previousCompanies: [...(e?.previousCompanies ?? [])],
      specialties: [...(e?.specialties ?? [])],
    },
  }
}

const cleanList = (values: string[]): string[] =>
  values.map((v) => v.trim()).filter((v, i, all) => v.length > 0 && all.indexOf(v) === i)

const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i])

/** A numeric input's text as the server expects it: '' clears, else a number. */
function numberPatch(value: string): number | '' | null {
  const trimmed = value.trim()
  if (!trimmed) return ''
  const n = Number(trimmed.replace(/[$,]/g, ''))
  return Number.isFinite(n) ? n : null
}

const STRING_KEYS = [
  'name',
  'company',
  'title',
  'entityType',
  'notes',
  'email',
  'phone',
  'directPhone',
  'mobilePhone',
  'officePhone',
  'address',
  'city',
  'state',
  'postalCode',
  'linkedIn',
  'website',
  'status',
  'companyDomain',
  'roleEvidence',
] as const

const BOOLEAN_KEYS = ['isEntity', 'isInternal', 'isPreferred', 'isBlocklisted'] as const
const LIST_KEYS = ['emails', 'tags', 'assetClasses', 'flags', 'emailDomains'] as const
const NUMBER_KEYS = ['yearsExperience', 'netWorth', 'liquidity'] as const

/**
 * Only what changed, as the server wants it.
 *
 * Strings are compared trimmed; an emptied string is sent as '' (clear), an
 * untouched blank is not sent at all. Lists are compared by value after
 * trimming and de-duplicating. Numbers that fail to parse are left out
 * rather than sent as NaN.
 */
export function buildPatch(initial: ContactFormValues, current: ContactFormValues): CrmContactPatch {
  const patch: CrmContactPatch = {}

  for (const key of STRING_KEYS) {
    const before = initial[key].trim()
    const after = current[key].trim()
    if (before !== after) patch[key] = after
  }
  if (initial.companyType !== current.companyType) patch.companyType = current.companyType
  for (const key of BOOLEAN_KEYS) {
    if (Boolean(initial[key]) !== Boolean(current[key])) patch[key] = Boolean(current[key])
  }
  for (const key of LIST_KEYS) {
    const before = cleanList(initial[key])
    const after = cleanList(current[key])
    if (!sameList(before, after)) patch[key] = after
  }
  for (const key of NUMBER_KEYS) {
    if (initial[key].trim() === current[key].trim()) continue
    const value = numberPatch(current[key])
    if (value !== null) patch[key] = value
  }

  const enrichment: NonNullable<CrmContactPatch['enrichment']> = {}
  if (initial.enrichment.yearsExperience.trim() !== current.enrichment.yearsExperience.trim()) {
    const value = numberPatch(current.enrichment.yearsExperience)
    if (value !== null) enrichment.yearsExperience = value
  }
  for (const key of ['dealVolume', 'education'] as const) {
    const before = initial.enrichment[key].trim()
    const after = current.enrichment[key].trim()
    if (before !== after) enrichment[key] = after
  }
  for (const key of ['licenses', 'previousCompanies', 'specialties'] as const) {
    const before = cleanList(initial.enrichment[key])
    const after = cleanList(current.enrichment[key])
    if (!sameList(before, after)) enrichment[key] = after
  }
  if (Object.keys(enrichment).length > 0) patch.enrichment = enrichment

  return patch
}

// ── Provenance ─────────────────────────────────────────────────────────────

export interface Provenance {
  source: string
  observedAt: string | null
}

/** Aliases between the screen's field names and the names capture stores. */
const PROVENANCE_ALIASES: Record<string, string[]> = {
  phone: ['phone', 'primary'],
  directPhone: ['directPhone', 'direct_phone', 'direct'],
  mobilePhone: ['mobilePhone', 'mobile_phone', 'mobile'],
  officePhone: ['officePhone', 'office_phone', 'office'],
  linkedIn: ['linkedIn', 'linkedin'],
  companyDomain: ['companyDomain', 'company_domain'],
  postalCode: ['postalCode', 'postal_code'],
  companyType: ['companyType', 'company_type'],
  roleEvidence: ['roleEvidence', 'role_evidence'],
}

/**
 * Where a displayed value came from, when anything recorded it.
 *
 * A hand edit outranks everything — the pinned field is the person's answer.
 * Otherwise the capture provenance the projection carries (`enrichment.extra`
 * for the slots the screen has no field for; `fieldSources` when a record
 * exposes it). Null means nothing on record says, and the tooltip says so.
 */
export function provenanceFor(contact: CrmContact, field: string): Provenance | null {
  const names = PROVENANCE_ALIASES[field] ?? [field]
  if ((contact.editedFields ?? []).some((f) => names.includes(f))) {
    return { source: `Edited by ${contact.editedBy ?? 'a person'}`, observedAt: null }
  }
  const pinned = contact.enrichment?.pinnedFields ?? []
  if (pinned.some((f) => names.includes(f))) {
    return { source: 'Set by hand', observedAt: null }
  }
  const extra = (contact.enrichment?.extra ?? []).find((row) => names.includes(row.field))
  if (extra && extra.source) return { source: extra.source, observedAt: extra.observedAt ?? null }
  // Some projections carry the capture engine's per-column provenance whole.
  const stored = (contact as { fieldSources?: Record<string, { source?: string | null; observedAt?: string | null }> })
    .fieldSources
  if (stored) {
    for (const name of names) {
      const entry = stored[name]
      if (entry?.source) return { source: entry.source, observedAt: entry.observedAt ?? null }
    }
  }
  return null
}

/** The tooltip for a value: its source and date, or that none is recorded. */
export function provenanceTitle(contact: CrmContact, field: string): string {
  const p = provenanceFor(contact, field)
  if (!p) return 'Provenance not recorded'
  return p.observedAt ? `${p.source} · ${formatDate(p.observedAt)}` : p.source
}

// ── Deals and files ────────────────────────────────────────────────────────

/** The recorded deal rows, plus a bare row for any linked id the history
 *  does not describe, so the count and the table never disagree. */
export function dealRowsFor(contact: Pick<CrmContact, 'dealHistory' | 'linkedDeals'>): CrmDealHistoryRow[] {
  const rows = [...(contact.dealHistory ?? [])]
  const seen = new Set(rows.map((r) => r.dealId))
  for (const id of contact.linkedDeals ?? []) {
    if (seen.has(id)) continue
    seen.add(id)
    rows.push({ dealId: id, property: null, amount: null, stage: null, date: null, outcome: null })
  }
  return rows
}

/** A message as the profile lists it, with every capacity the contact held on it. */
export interface ListedCommunication extends CrmCommunication {
  capacities: string[]
}

/**
 * One row per message.
 *
 * A merged record carries a copy of the same message from each contact folded
 * into it — the sponsor's principal and its acquisitions lead were both on the
 * thread — so the list read every message twice. The same intake record, or a
 * hand-logged entry with the same subject, time, deal and direction, is one
 * message; the capacities are kept together. An undated hand-logged entry is
 * never folded, since nothing on it says two of them are the same event.
 */
export function distinctCommunications(comms: readonly CrmCommunication[]): ListedCommunication[] {
  const byKey = new Map<string, ListedCommunication>()
  for (const comm of comms) {
    const key = comm.ingestionId
      ? `intake:${comm.ingestionId}`
      : comm.timestamp
        ? ['logged', comm.subject ?? comm.type, comm.timestamp, comm.dealId ?? '', comm.direction ?? ''].join('|')
        : `entry:${comm.id}`
    const seen = byKey.get(key)
    if (seen) {
      if (comm.capacity && !seen.capacities.includes(comm.capacity)) seen.capacities.push(comm.capacity)
      continue
    }
    byKey.set(key, { ...comm, capacities: comm.capacity ? [comm.capacity] : [] })
  }
  return [...byKey.values()]
}

/** The stored file to open first for a message: the email itself, else
 *  whatever is there. */
export function initialShaFor(files: { kind: string; sha256: string; filename?: string }[]): string | undefined {
  return files.find((f) => f.kind === 'email_source')?.sha256 ?? files[0]?.sha256
}

// ── Formatting ─────────────────────────────────────────────────────────────

export function relativeTime(isoDate: string | null | undefined): string {
  // Nothing recorded is not "just now" — it is nothing recorded.
  if (!isoDate) return NOT_ON_RECORD
  const now = new Date()
  const d = new Date(isoDate)
  if (Number.isNaN(d.getTime())) return NOT_ON_RECORD
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay === 1) return '1 day ago'
  if (diffDay < 30) return `${diffDay}d ago`
  return `${Math.floor(diffDay / 30)}mo ago`
}

/** A short absolute date for a table cell; the input when it will not parse. */
export function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return NOT_ON_RECORD
  // A date-only value is that calendar day here, not midnight UTC.
  const d = parseDateValue(isoDate)
  if (!d) return String(isoDate)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Date and time, for an activity row. */
export function formatStamp(isoDate: string | null | undefined): string {
  if (!isoDate) return NOT_ON_RECORD
  const d = new Date(isoDate)
  if (Number.isNaN(d.getTime())) return String(isoDate)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function initials(first: string, last: string): string {
  // An entity ("Harbor Point Owner LLC") has no surname, and `last[0]` on an
  // empty string is undefined — which rendered as the literal "MUNDEFINED".
  const a = first.trim()[0] ?? ''
  const b = last.trim()[0] ?? first.trim()[1] ?? ''
  return `${a}${b}`.toUpperCase()
}

export function contactInitials(contact: Pick<CrmContact, 'firstName' | 'lastName' | 'displayName' | 'email' | 'company'>): string {
  const fromName = initials(contact.firstName ?? '', contact.lastName ?? '')
  if (fromName) return fromName
  const fallback = crmContactName(contact)
  return initials(fallback, '')
}

export function typeBadgeColor(type: CrmCompanyType | string): string {
  switch (type) {
    case 'Broker':
      return 'bg-blue-400/10 text-blue-300 border-blue-400/20'
    case 'Borrower/Sponsor':
      return 'bg-emerald-400/10 text-emerald-300 border-emerald-400/20'
    case 'Investor':
      return 'bg-purple-400/10 text-purple-300 border-purple-400/20'
    case 'Law Firm':
      return 'bg-amber-400/10 text-amber-300 border-amber-400/20'
    case 'Appraiser':
      return 'bg-cyan-400/10 text-cyan-300 border-cyan-400/20'
    case 'Insurance':
      return 'bg-orange-400/10 text-orange-300 border-orange-400/20'
    case 'Internal':
      return 'bg-stone-400/10 text-stone-300 border-stone-400/20'
    // Captured off a deal, with nothing on record settling the side of the
    // table. Neutral on purpose: it must not read as a category.
    default:
      return 'bg-stone-500/10 text-stone-400 border-stone-500/20'
  }
}

/**
 * Every number on record, under the label the correspondence carried.
 *
 * A signature listing a direct line and a switchboard states two different
 * things. Where nothing labelled a number it appears once, unlabelled, rather
 * than being presented as a direct line it was never claimed to be.
 */
export function labelledPhones(
  contact: Pick<CrmContact, 'phone' | 'phones'>,
): { label: string; field: string; value: string | null }[] {
  const phones = contact.phones
  const rows: { label: string; field: string; value: string | null }[] = []
  if (phones?.direct) rows.push({ label: 'Direct', field: 'directPhone', value: phones.direct })
  if (phones?.mobile) rows.push({ label: 'Mobile', field: 'mobilePhone', value: phones.mobile })
  if (phones?.office) rows.push({ label: 'Office', field: 'officePhone', value: phones.office })
  if (rows.length === 0) {
    rows.push({ label: '', field: 'phone', value: contact.phone ?? phones?.primary ?? null })
  }
  return rows
}

export function commIcon(type: string): LucideIcon {
  switch (type) {
    case 'Call':
      return PhoneCall
    case 'Meeting':
      return CalendarDays
    case 'Note':
      return StickyNote
    default:
      return Mail
  }
}

/** The one-line result of a lookup, in the words every surface uses. */
export function enrichResultNote(result: Pick<CrmEnrichResponse, 'changedFields' | 'sources' | 'cacheHit'>): string {
  // A cache hit answered from the last real run, not a fresh one — pressing
  // Re-enrich twice inside the cache window must not read as two lookups.
  if (result.cacheHit) {
    return result.changedFields > 0
      ? `Served from the last lookup; updated ${result.changedFields} field${result.changedFields === 1 ? '' : 's'}.`
      : 'Served from the last lookup — nothing to add.'
  }
  if (result.changedFields > 0) return `Updated ${result.changedFields} field${result.changedFields === 1 ? '' : 's'}.`
  const answered = (result.sources ?? []).some((s) => s.ok)
  return answered ? 'Lookup completed; nothing new was found.' : 'Lookup completed; no source answered.'
}

/** The actor an operator-override observation names, when it names one. */
export function editedByActor(source: string | null | undefined): string | null {
  const m = /^Edited by\s+(.+)$/i.exec(String(source ?? '').trim())
  return m ? m[1].trim() : null
}

/** "Not stored on a sponsor master: title, status." */
export function ignoredNote(ignored: string[], recordKind: string): string | null {
  if (!ignored || ignored.length === 0) return null
  return `Not stored on a ${recordKind}: ${ignored.map(labelForField).join(', ')}.`
}

// ── Activity feed and dedup surface ────────────────────────────────────────

export const FEED_PAGE = 40

/** The feed's chips, each a set of kinds the log writes. Dedup covers what
 *  the pass does — automatic merges, name hygiene, and the merges a person
 *  settles; Edits covers every change a person makes to a record. */
export const FEED_FILTERS: { label: string; kinds: CrmActivityKind[] | null }[] = [
  { label: 'All', kinds: null },
  { label: 'Capture', kinds: ['capture'] },
  { label: 'Dedup', kinds: ['dedupe', 'hygiene', 'merge', 'unmerge'] },
  { label: 'Enrich', kinds: ['enrich'] },
  { label: 'Edits', kinds: ['edit', 'note', 'create', 'retire', 'restore'] },
  { label: 'Lists', kinds: ['list'] },
]

const stampOf = (a: CrmActivity) => (a.createdAt ? new Date(a.createdAt).getTime() : 0)

export interface FeedPage {
  items: CrmActivity[]
  hasMore: boolean
}

/** One page of the feed for a chip: one request per kind, merged newest
 *  first, so a chip that spans kinds still pages by time. */
export async function readFeedPage(kinds: CrmActivityKind[] | null, before: string | undefined): Promise<FeedPage> {
  if (!kinds) {
    const page = await crmService.getActivity({ limit: FEED_PAGE, before })
    return { items: page.items, hasMore: page.items.length >= FEED_PAGE }
  }
  const pages = await Promise.all(kinds.map((kind) => crmService.getActivity({ limit: FEED_PAGE, kind, before })))
  const merged = pages
    .flatMap((p) => p.items)
    .sort((a, b) => stampOf(b) - stampOf(a))
    .slice(0, FEED_PAGE)
  return { items: merged, hasMore: pages.some((p) => p.items.length >= FEED_PAGE) }
}

/** "Read 31 messages and 12 deals; 75 contacts on record (3 merged, 5 names cleaned)." */
export function captureSentence(s: CrmCaptureSummary): string {
  const n = (v: number | undefined) => v ?? 0
  const extras = [`${n(s.contactsMerged)} merged`, `${n(s.namesCleaned)} names cleaned`]
  let sentence = `Read ${n(s.recordsRead)} message${n(s.recordsRead) === 1 ? '' : 's'} and ${n(s.dealsRead)} deal${n(s.dealsRead) === 1 ? '' : 's'}; ${n(s.contactsOnRecord)} contacts on record (${extras.join(', ')}).`
  // What the sweep could not do — never absorbed into a total that reads as
  // a clean run when part of it failed.
  const failed = [
    s.recordsFailed ? `${s.recordsFailed} message${s.recordsFailed === 1 ? '' : 's'}` : null,
    s.dealsFailed ? `${s.dealsFailed} deal${s.dealsFailed === 1 ? '' : 's'}` : null,
  ].filter((x): x is string => Boolean(x))
  if (failed.length > 0) sentence += ` ${failed.join(' and ')} could not be read.`
  if (s.dedupFailed) sentence += ' The dedup pass could not complete.'
  return sentence
}

/** Whether a capture summary carries a failure the note should be coloured for. */
export function captureHadFailure(s: CrmCaptureSummary): boolean {
  return Boolean(s.recordsFailed || s.dealsFailed || s.dedupFailed)
}

export function dedupSentence(s: CrmDedupSummary): string {
  const parts = [`${s.merged} merged`, `${s.renamed} name${s.renamed === 1 ? '' : 's'} cleaned`, `${s.cleared} cleared`]
  if (s.retired) parts.push(`${s.retired} retired`)
  // Nothing is left for a person (4 Nov 2026): a near pair that met no merge
  // rule simply stays two records.
  return `Dedup ran: ${parts.join(', ')}; ${s.candidates} near pair${s.candidates === 1 ? '' : 's'} kept apart.`
}

/** Which of a duplicate pair can survive a merge: the source must be a
 *  captured row, so a master in the pair is the only possible survivor. */
export function survivorsFor(c: CrmDuplicateCandidate): { keep: string; keepName: string; fold: string }[] {
  const [a, b] = c.ids
  const [an, bn] = c.names
  const out: { keep: string; keepName: string; fold: string }[] = []
  if (partyTypeFor(b) === 'deal_contact') out.push({ keep: a, keepName: an, fold: b })
  if (partyTypeFor(a) === 'deal_contact') out.push({ keep: b, keepName: bn, fold: a })
  return out
}
